<template>
  <div class="space-y-6">
    <!-- 页面标题 -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-700">供应商回调管理</h2>
      <div class="flex items-center mt-2">
        <router-link to="/admin/dashboard" class="text-gray-600 hover:text-gray-800">首页</router-link>
        <span class="mx-2 text-gray-400">•</span>
        <router-link to="/admin/system/performance" class="text-gray-600 hover:text-gray-800">系统管理</router-link>
        <span class="mx-2 text-gray-400">•</span>
        <span class="text-gray-600">供应商回调管理</span>
      </div>
    </div>
    
    <!-- 搜索表单 -->
    <div class="bg-white shadow rounded-lg overflow-hidden">
      <SearchFilters
        :filter-config="filterConfig"
        :initial-filters="filters"
        @search="search"
        @reset="resetFilters"
        @update:filters="updateFilters"
      />
    </div>
    
    <!-- 主内容区域 -->
    <div class="bg-white shadow rounded-lg overflow-hidden">
      <!-- 错误提示 -->
      <div v-if="errorMessage" class="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
        <div class="font-medium">错误!</div>
        <div>{{ errorMessage }}</div>
      </div>
      
      <!-- 结果计数 -->
      <div class="px-6 py-3 bg-gray-50 border-b border-gray-200">
        <div class="text-sm text-gray-500">
          <span v-if="!filters.callback_id">请输入回调ID进行查询</span>
          <span v-else-if="totalItems > 0">找到 <span class="font-semibold">{{ totalItems }}</span> 条回调记录
            <template v-if="filters.vendor">
              (供应商: <span class="font-semibold">{{ filters.vendor }}</span>)
            </template>
          </span>
          <span v-else>没有找到匹配的回调记录</span>
        </div>
      </div>
      
      <!-- 回调记录表格 -->
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                时间
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                回调ID
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                供应商
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                状态
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                耗时
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                URL
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="loading">
              <td colspan="7" class="px-6 py-10 text-center text-gray-500">
                <div class="flex flex-col items-center">
                  <div class="loader mb-3"></div>
                  <p>加载中...</p>
                </div>
              </td>
            </tr>
            <tr v-else-if="callbacks.length === 0">
              <td colspan="7" class="px-6 py-10 text-center text-gray-500">
                <div class="flex flex-col items-center">
                  <i class="fas fa-search text-gray-400 text-3xl mb-3"></i>
                  <p>没有找到回调记录</p>
                  <p class="mt-1 text-sm">请尝试其他搜索条件或确保已输入回调ID</p>
                </div>
              </td>
            </tr>
            <tr v-for="callback in callbacks" :key="callback.callback_id">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ callback.timestamp || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ callback.callback_id || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ callback.vendor || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="['px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full', getStatusClass(callback.status)]">
                  {{ getStatusText(callback.status) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ callback.duration || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                {{ callback.url || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <button @click="showDetail(callback.log_stream, callback.ptr, callback.vendor)" 
                        class="text-blue-600 hover:text-blue-900 mr-3">
                  查看
                </button>
                <button v-if="callback.status !== 1"
                        @click="resendCallback(callback.callback_id)"
                        class="text-red-600 hover:text-red-900">
                  重发
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <!-- 回调详情模态框 -->
    <Modal
      :visible="detailModalVisible"
      title="回调详情"
      @close="closeModal"
      max-width="4xl"
    >
      <div class="flex flex-col space-y-4">
        <div v-if="detailLoading" class="text-center py-10">
          <div class="loader mx-auto"></div>
          <p class="mt-2 text-gray-600">加载中...</p>
        </div>
        
        <div v-else-if="detailError" class="text-center py-10">
          <i class="fas fa-exclamation-circle text-red-600 text-2xl"></i>
          <p class="mt-2 text-red-600">{{ detailError }}</p>
        </div>
        
        <div v-else-if="callbackDetail" class="divide-y divide-gray-200">
          <div class="grid grid-cols-2 gap-4 mb-4 pb-4">
            <div>
              <span class="text-sm font-medium text-gray-700">时间:</span>
              <span class="ml-2 text-sm text-gray-900">{{ callbackDetail.timestamp }}</span>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-700">供应商:</span>
              <span class="ml-2 text-sm text-gray-900">{{ callbackDetail.vendor }}</span>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-700">回调ID:</span>
              <span class="ml-2 text-sm text-gray-900">{{ callbackDetail.callback_id }}</span>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-700">状态:</span>
              <span class="ml-2 text-sm text-gray-900">{{ callbackDetail.status }}</span>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-700">耗时:</span>
              <span class="ml-2 text-sm text-gray-900">{{ callbackDetail.duration }}</span>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-700">来源IP:</span>
              <span class="ml-2 text-sm text-gray-900">{{ callbackDetail.ip }}</span>
            </div>
          </div>
          
          <div class="mb-4 pt-4 pb-4">
            <h4 class="text-sm font-medium text-gray-700 mb-2">请求内容:</h4>
            <pre class="bg-gray-50 p-3 rounded text-sm text-gray-800 overflow-auto max-h-40">{{ callbackDetail.request }}</pre>
          </div>
          
          <div class="pt-4">
            <h4 class="text-sm font-medium text-gray-700 mb-2">响应内容:</h4>
            <pre class="bg-gray-50 p-3 rounded text-sm text-gray-800 overflow-auto max-h-40">{{ callbackDetail.response }}</pre>
          </div>
        </div>
      </div>
      
      <template #footer>
        <button @click="closeModal" class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
          关闭
        </button>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import SearchFilters from '@/components/common/SearchFilters.vue';
import Modal from '@/components/common/Modal.vue';
import AdminApiClient from '@/utils/admin-api-client';

const apiClient = new AdminApiClient();

// 状态
const loading = ref(false);
const callbacks = ref([]);
const totalItems = ref(0);
const errorMessage = ref('');

// 详情模态框状态
const detailModalVisible = ref(false);
const detailLoading = ref(false);
const callbackDetail = ref(null);
const detailError = ref('');

// 筛选条件
const filters = reactive({
  callback_id: '',
  vendor: '',
  start_time: '',
  end_time: ''
});

// 筛选器配置
const filterConfig = reactive([
  {
    id: 'callback_id',
    type: 'input',
    label: '回调ID',
    placeholder: '输入回调ID',
    icon: 'fas fa-hashtag'
  },
  {
    id: 'vendor',
    type: 'select',
    label: '供应商',
    placeholder: '选择供应商',
    icon: 'fas fa-building',
    options: [
      // 将在loadVendors方法中动态填充
    ]
  },
  {
    id: 'date_range',
    type: 'dateRange',
    label: '日期范围',
    placeholder: '选择日期范围',
    icon: 'fas fa-calendar',
    startField: 'start_time',
    endField: 'end_time'
  }
]);

// 获取状态样式类
function getStatusClass(status) {
  // 处理数字状态码
  if (typeof status === 'number') {
    switch(status) {
      case 0: return 'bg-yellow-100 text-yellow-800'; // pending
      case 1: return 'bg-green-100 text-green-800';   // success
      case 2: return 'bg-red-100 text-red-800';       // failed
      case 3: return 'bg-red-100 text-red-800';       // error
      default: return 'bg-gray-100 text-gray-800';    // unknown
    }
  }
  
  // 原有的字符串状态处理
  const statusClasses = {
    'success': 'bg-green-100 text-green-800',
    'error': 'bg-red-100 text-red-800',
    'failed': 'bg-red-100 text-red-800',
    'pending': 'bg-yellow-100 text-yellow-800'
  };
  
  return statusClasses[String(status).toLowerCase()] || 'bg-gray-100 text-gray-800';
}

// 获取状态文本
function getStatusText(status) {
  // 处理数字状态码
  if (typeof status === 'number') {
    switch(status) {
      case 0: return '待处理';
      case 1: return '成功';
      case 2: return '失败';
      case 3: return '错误';
      default: return '未知';
    }
  }
  
  return status; // 如果已经是文本，则直接返回
}

// 加载供应商列表
async function loadVendors() {
  try {
    const response = await apiClient.getVendors();
    if (response.success && response.vendors) {
      // 添加供应商选项
      const vendorOptions = [];
      
      Object.entries(response.vendors).forEach(([value, name]) => {
        vendorOptions.push({ value, label: name });
      });
      
      // 更新筛选器配置
      const vendorFilter = filterConfig.find(f => f.id === 'vendor');
      if (vendorFilter) {
        vendorFilter.options = vendorOptions;
      }
    }
  } catch (error) {
    console.error('加载供应商列表失败:', error);
  }
}

// 搜索回调数据
async function search(newFilters) {
  Object.assign(filters, newFilters);
  
  if (!filters.callback_id) {
    callbacks.value = [];
    totalItems.value = 0;
    return;
  }
  
  loading.value = true;
  errorMessage.value = '';
  
  try {
    const response = await apiClient.getVendorCallbacks(filters);
    
    if (!response.success) {
      errorMessage.value = response.message || '获取数据失败';
      callbacks.value = [];
      totalItems.value = 0;
    } else {
      // 处理成功响应
      if (response.data && response.data.callbacks) {
        callbacks.value = response.data.callbacks || [];
        totalItems.value = response.data.totalCount || 0;
      } else {
        callbacks.value = [];
        totalItems.value = 0;
        console.error('无效的数据格式');
      }
    }
  } catch (error) {
    console.error('加载回调数据失败:', error);
    errorMessage.value = '加载回调数据失败: ' + error.message;
    callbacks.value = [];
    totalItems.value = 0;
  } finally {
    loading.value = false;
  }
}

// 重置筛选条件
function resetFilters() {
  Object.keys(filters).forEach(key => {
    filters[key] = '';
  });
  
  callbacks.value = [];
  totalItems.value = 0;
  errorMessage.value = '';
}

// 监听过滤器更新
function updateFilters(newFilters) {
  // 更新本地过滤器状态
  Object.keys(newFilters).forEach(key => {
    if (newFilters[key] !== undefined) {
      filters[key] = newFilters[key];
    }
  });
}

// 显示回调详情
async function showDetail(logStream, ptr, vendor) {
  detailModalVisible.value = true;
  detailLoading.value = true;
  callbackDetail.value = null;
  detailError.value = '';
  
  try {
    const response = await apiClient.getCallbackDetail({
      callback_id: logStream,
      log_stream: logStream,
      ptr: ptr,
      vendor: vendor
    });
    
    if (!response.success) {
      detailError.value = response.message || '获取详情失败';
    } else if (response.data) {
      callbackDetail.value = response.data;
    } else {
      detailError.value = '返回数据格式错误';
    }
  } catch (error) {
    console.error('获取回调详情失败:', error);
    detailError.value = '获取详情失败: ' + error.message;
  } finally {
    detailLoading.value = false;
  }
}

// 关闭模态框
function closeModal() {
  detailModalVisible.value = false;
}

// 重发回调
async function resendCallback(callbackId) {
  if (!confirm(`确定要重发回调请求 ${callbackId} 吗？`)) {
    return;
  }
  
  try {
    const response = await apiClient.resendCallback({ callback_id: callbackId });
    
    if (response.success) {
      alert(response.message || '重发成功');
      // 重新加载回调数据
      search(filters);
    } else {
      alert(`重发失败: ${response.message || '未知错误'}`);
    }
  } catch (error) {
    console.error('重发请求失败:', error);
    alert(`重发请求失败: ${error.message}`);
  }
}

// 页面初始化
onMounted(() => {
  // 加载供应商列表
  loadVendors();
  
  // 如果URL中有callbackId参数，自动加载数据
  const urlParams = new URLSearchParams(window.location.search);
  const callbackId = urlParams.get('callback_id');
  
  if (callbackId) {
    filters.callback_id = callbackId;
    search(filters);
  }
});
</script>

<style scoped>
.status-success {
  @apply bg-green-100 text-green-800;
}

.status-error, .status-failed {
  @apply bg-red-100 text-red-800;
}

.status-pending {
  @apply bg-yellow-100 text-yellow-800;
}

.status-unknown {
  @apply bg-gray-100 text-gray-500;
}

.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 